import { Landmark, Handshake, TrendingDown, Award } from "lucide-react";
import { GoldBadge, SectionHead, Bullets, Reveal } from "@/components/shared";

const MILESTONES = [
  {
    icon: Landmark,
    date: "31 MAY 2017 — ADELAIDE, SA",
    title: "The Royal Commission Victory",
    description:
      "A landmark private session held in Adelaide, logging 45 years of historical institutional data permanently into the nation's records.",
    bullets: [
      "Private session held in Adelaide, South Australia",
      "45 years of institutional history entered into the permanent national record",
      "A survivor's testimony formally acknowledged by Australia's highest inquiry",
      "The moment decades of silence became an irreversible public record",
    ],
    testId: "milestone-royal-commission",
  },
  {
    icon: Handshake,
    date: "ONGOING — CROSS-CONTINENTAL",
    title: "Institutional Accountability",
    description:
      "Forcing cross-continental institutional leaders to stand face-to-face with their actions — resulting in official, formal apologies.",
    bullets: [
      "Institutional leaders confronted face-to-face with the consequences of their actions",
      "Official, formal apologies secured from the institutions responsible",
      "Accountability pursued across state and national boundaries",
      "No settlement of reputation over the safety of children",
    ],
    testId: "milestone-accountability",
  },
  {
    icon: TrendingDown,
    date: "10+ YEARS — REGISTERED OPERATION",
    title: "Community Impact",
    description:
      "Over a decade of registered non-profit operation, actively reducing local risk factors through targeted public-safety initiatives.",
    bullets: [
      "More than a decade of continuous registered non-profit operation",
      "Measurable reduction in local risk factors through targeted patrols",
      "Public-safety initiatives embedded in the communities we serve",
      "A standing, visible deterrent against abuse and neglect",
    ],
    testId: "milestone-community-impact",
  },
];

const HIGHLIGHTS = [
  { icon: Award, tag: "Winner — 2023", title: "Commonwealth Bank Community Organisation Award", testId: "highlight-commbank" },
  { icon: Landmark, tag: "Semi-Finalist — 2023", title: "Lifetime Achievement Award, Community Achievement Awards", testId: "highlight-lifetime" },
  { icon: Handshake, tag: "Nominee — 2020", title: "Australian of the Year", testId: "highlight-australian-of-year" },
];

const HONOURS = [
  { year: "2014", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
  ]},
  { year: "2015", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
    { label: "NAPCAN Play Your Part Award", tag: "Nominee" },
  ]},
  { year: "2016", items: [
    { label: "Statewide Superannuation Super Hero Award", tag: "Nominee" },
    { label: "National Australia Bank Community Organisation Award", tag: "Nominee" },
  ]},
  { year: "2017", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
    { label: "Human Rights Award", tag: "Nominee" },
    { label: "NAPCAN Play Your Part Award", tag: "Nominee" },
  ]},
  { year: "2018", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
    { label: "Human Rights Award", tag: "Nominee" },
    { label: "NAPCAN Play Your Part Award", tag: "Nominee" },
  ]},
  { year: "2019", items: [
    { label: "SA Community Achievement Awards — Community Organisation (S.O.P.)", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
  ]},
  { year: "2020", items: [
    { label: "Australian of the Year", tag: "Nominee" },
  ]},
  { year: "2021", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (S.O.P.)", tag: "Nominee" },
    { label: "Human Rights Award", tag: "Nominee" },
    { label: "NAPCAN Play Your Part Award", tag: "Nominee" },
  ]},
  { year: "2022", items: [
    { label: "SA Community Achievement Awards — Volunteer Award", tag: "Nominee" },
    { label: "SA Community Achievement Awards — Community Organisation (S.O.P.)", tag: "Nominee" },
    { label: "NAPCAN Child Protection Awards (×2)", tag: "Nominee" },
    { label: "Human Rights Award", tag: "Nominee" },
  ]},
  { year: "2023", items: [
    { label: "Community Achievement Awards — Community Organisation (P.A.I.N.)", tag: "Nominee" },
    { label: "Community Achievement Awards — Community Organisation (S.O.P.)", tag: "Nominee" },
    { label: "Community Achievement Awards — Lifetime Achievement Award", tag: "Semi-Finalist" },
    { label: "Commonwealth Bank Community Organisation Award", tag: "Winner" },
    { label: "NAPCAN Child Protection Awards (×3)", tag: "Nominee" },
    { label: "NAPCAN Play Your Part Award", tag: "Nominee" },
    { label: "Human Rights Award", tag: "Nominee" },
  ]},
];

const tagCls = (tag) =>
  tag === "Winner"
    ? "bg-amber-500/20 text-amber-300 border-amber-500/60"
    : tag === "Semi-Finalist"
    ? "bg-blue-500/15 text-blue-300 border-blue-500/40"
    : "bg-white/5 text-slate-400 border-white/15";

export default function Achievements() {
  return (
    <div data-testid="achievements-page">
      <section className="border-b border-white/10 bg-[#111622] grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <GoldBadge testId="achievements-badge">Systemic Impact, On the Record</GoldBadge>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50">
              Achievements, Awards <span className="gold-text-gradient">& Nominations</span>
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg leading-relaxed text-slate-300">
              Our organisational trajectory is not measured in headlines — it is measured in
              permanent records, formal apologies, and children who are safer tonight.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="relative space-y-10 before:absolute before:left-[7px] sm:before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-amber-500/60 before:via-amber-500/20 before:to-transparent">
          {MILESTONES.map((m, i) => (
            <Reveal key={m.testId} delay={i * 0.05}>
              <article data-testid={m.testId} className="relative pl-8 sm:pl-12">
                <span className="absolute left-0 top-2 h-4 w-4 rotate-45 bg-amber-500 shadow-[0_0_14px_rgba(245,158,11,0.6)]" />
                <div className="card-pain p-6 sm:p-10">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="flex items-center justify-center h-12 w-12 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                      <m.icon className="h-6 w-6 text-amber-400" />
                    </span>
                    <span className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90">
                      {m.date}
                    </span>
                  </div>
                  <h2 className="font-display mt-5 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50">
                    {m.title}
                  </h2>
                  <p className="mt-3 text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl">
                    {m.description}
                  </p>
                  <div className="mt-6">
                    <Bullets items={m.bullets} />
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#111622]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <SectionHead
              eyebrow="Honours Roll — 2014 to 2023"
              title="A Decade of Awards, Nominations & Commendations"
              description="Ten consecutive years of formal recognition for Ronald Waters and the organisation's frontline work — culminating in the 2023 Commonwealth Bank Community Organisation Award win."
            />
          </Reveal>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.testId} delay={i * 0.08}>
                <div data-testid={h.testId} className="card-pain p-6 h-full">
                  <h.icon className="h-7 w-7 text-amber-400" />
                  <p className="mt-4 font-code text-xs uppercase tracking-[0.2em] text-amber-400/90">{h.tag}</p>
                  <h3 className="font-display mt-2 text-lg font-medium text-slate-50 leading-snug">{h.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 space-y-5">
            {HONOURS.map((h) => (
              <Reveal key={h.year}>
                <div
                  data-testid={`honours-${h.year}`}
                  className="card-pain p-6 sm:p-8 grid gap-4 md:grid-cols-[110px_1fr]"
                >
                  <span className="font-display text-2xl font-bold gold-text-gradient">{h.year}</span>
                  <ul className="space-y-2.5">
                    {h.items.map((it) => (
                      <li
                        key={it.label}
                        className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm sm:text-base text-slate-300"
                      >
                        <span className="h-1.5 w-1.5 rotate-45 bg-amber-500 shrink-0" aria-hidden="true" />
                        <span className="flex-1 min-w-[200px]">{it.label}</span>
                        <span
                          className={`text-[10px] font-code uppercase tracking-wider px-2 py-0.5 rounded-full border ${tagCls(it.tag)}`}
                        >
                          {it.tag}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
